import * as React from "react";

import { TooltipContext, type TooltipTrigger, type TooltipProviderState } from "./TooltipProvider";

type Props = {
    content_key?: string;
    content?: React.ReactNode;
    children?: React.ReactNode;
    class_name: string;
    offset_x: number;
    offset_y: number;
    delay: number;
    style: React.CSSProperties;
    interactive: boolean;
    container?: Element | null;
    trigger: TooltipTrigger;
};

const DATA_HAS_TOOLTIP_ATTR = "data-has-tooltip";

function hasTooltip(element: Element) {
    return element.getAttribute(DATA_HAS_TOOLTIP_ATTR) === "true";
}

function shouldProcessMouseEvent(event: React.MouseEvent<Element>) {
    const { target, currentTarget } = event;
    if (target instanceof Element && (target === currentTarget || !hasTooltip(target))) {
        return true;
    }

    return false;
}

function getEventHandler<T>(element: React.ReactElement, handlerName: string, handler: (event: T) => void) {
    const existingHandler = element.props && element.props[handlerName];

    if (typeof existingHandler !== "function") {
        return handler;
    }

    // Merge event handlers
    return (event: T) => {
        existingHandler(event);
        handler(event);
    };
}

/**
 * Wraps children with hover or click handlers that drive the shared tooltip via context.
 * Supports show delay, follow-cursor positioning, interactive content, and click-to-toggle.
 */
class Tooltip extends React.Component<Props> {
    static contextType = TooltipContext;
    static defaultProps: Props = {
        class_name: "",
        offset_x: 10,
        offset_y: 15,
        delay: 500,
        style: {},
        interactive: true,
        trigger: "hover",
    };
    context!: TooltipProviderState;

    show_timeout?: ReturnType<typeof setTimeout>;
    ref: Element | null = null;

    componentWillUnmount() {
        clearTimeout(this.show_timeout);
        if (this.context.isActiveTarget(this.ref)) {
            this.context.updateState(this.ref, this.getContainer(), {
                show: false,
                content: null,
            });
        }
        this.ref = null;
    }

    componentDidUpdate(prev_props: Props) {
        if (this.props.content_key !== prev_props.content_key && this.context.isActiveTarget(this.ref)) {
            this.context.updateState(this.ref, this.getContainer(), {
                class_name: this.props.class_name,
                offset_x: this.props.offset_x,
                offset_y: this.props.offset_y,
                style: this.props.style,
                content: this.props.content,
            });
        }
    }

    getContainer = (): Element | null => {
        return this.props.container || document.body;
    };

    updateRef = (element: Element) => {
        if (this.ref === null) {
            this.ref = element;
        }
    };

    handleMouseOver = (event: React.MouseEvent<Element>) => {
        this.updateRef(event.currentTarget);
        if (!shouldProcessMouseEvent(event) || this.props.trigger !== "hover") {
            return;
        }

        clearTimeout(this.show_timeout);
        const { delay, ...other_props } = this.props;
        this.context.updateState(this.ref, this.getContainer(), {
            ...other_props,
            show: false,
        });
        this.show_timeout = setTimeout(() => {
            if (this.context.isActiveTarget(this.ref)) {
                this.context.updateState(this.ref, this.getContainer(), {
                    show: true,
                    x: event.clientX,
                    y: event.clientY,
                });
            }
        }, delay);
    };

    handleMouseOut = (event: React.MouseEvent<Element>) => {
        this.updateRef(event.currentTarget);
        if (!shouldProcessMouseEvent(event) || this.props.trigger !== "hover") {
            return;
        }

        clearTimeout(this.show_timeout);
        this.show_timeout = setTimeout(() => {
            if (this.context.isActiveTarget(this.ref)) {
                this.context.updateState(this.ref, this.getContainer(), {
                    show: false,
                });
            }
        }, 0);
    };

    handleMouseMove = (event: React.MouseEvent<Element>) => {
        this.updateRef(event.currentTarget);
        if (!shouldProcessMouseEvent(event) || this.props.trigger !== "hover") {
            return;
        }

        clearTimeout(this.show_timeout);
        this.show_timeout = setTimeout(() => {
            if (!this.show_timeout || !this.context.isActiveTarget(this.ref)) {
                return;
            }

            this.context.updateState(this.ref, this.getContainer(), {
                show: true,
                x: event.clientX,
                y: event.clientY,
            });
        }, this.props.delay);
    };

    handleMouseDown = (event: React.MouseEvent<Element>) => {
        this.updateRef(event.currentTarget);
        if (!shouldProcessMouseEvent(event)) {
            return;
        }

        if (this.props.trigger === "hover") {
            clearTimeout(this.show_timeout);
            this.context.updateState(this.ref, this.getContainer(), {
                show: false,
            });
        }
    };

    handleClick = (event: React.MouseEvent<Element>) => {
        this.updateRef(event.currentTarget);
        if (!shouldProcessMouseEvent(event)) {
            return;
        }

        if (this.props.trigger === "click") {
            clearTimeout(this.show_timeout);
            this.context.updateState(this.ref, this.getContainer(), {
                ...this.props,
                show: !this.context.show,
                x: event.clientX,
                y: event.clientY,
            });
        }
    };

    handleBlur = (event: React.FocusEvent<Element>) => {
        this.updateRef(event.currentTarget);
        if (this.props.trigger !== "click") {
            return;
        }

        clearTimeout(this.show_timeout);
        this.show_timeout = setTimeout(() => {
            if (this.context.isActiveTarget(this.ref)) {
                this.context.updateState(this.ref, this.getContainer(), {
                    show: false,
                });
            }
        }, 0);
    };

    handleKeyDown = (event: React.KeyboardEvent<Element>) => {
        this.updateRef(event.currentTarget);
        if (this.props.trigger !== "click") {
            return;
        }

        if (event.key === "Escape") {
            this.context.updateState(this.ref, this.getContainer(), {
                show: false,
            });
        }
    };

    render() {
        const { children } = this.props;

        if (!children) {
            return null;
        }

        return React.Children.map(children, (child: React.ReactNode) => {
            if (child && React.isValidElement(child)) {
                const childProps = (child.props || {}) as {
                    role?: string;
                    tabIndex?: string;
                };
                return React.cloneElement(child, {
                    onMouseOver: getEventHandler(child, "onMouseOver", this.handleMouseOver),
                    onMouseOut: getEventHandler(child, "onMouseOut", this.handleMouseOut),
                    onMouseMove: getEventHandler(child, "onMouseMove", this.handleMouseMove),
                    onMouseDown: getEventHandler(child, "onMouseDown", this.handleMouseDown),
                    onClick: getEventHandler(child, "onClick", this.handleClick),
                    onBlur: getEventHandler(child, "onBlur", this.handleBlur),
                    onKeyDown: getEventHandler(child, "onKeyDown", this.handleKeyDown),
                    role: this.props.trigger === "click" ? "button" : childProps.role,
                    tabIndex: childProps.tabIndex ?? (this.props.trigger === "click" ? "0" : undefined),
                    [DATA_HAS_TOOLTIP_ATTR]: "true",
                } as Partial<React.ReactElement>);
            }

            return child;
        });
    }
}

export default Tooltip;
